import {
  gql,
} from '@apollo/client';
import IAppointment from '@monorepo-zazuu/shared/interfaces/IAppointment';
import IProfessional from '@monorepo-zazuu/shared/interfaces/IProfessional';
import client from './api-graphql';

interface IFiltersAppointments {
  pagination?: {
    limit: number;
    page: number;
  };
  orderBy?: {
    [key: string]: 'asc' | 'desc';
  }[];
  where: {
    from?: string;
    to?: string;
    statusNotEqual?: number;
  };
}

const searchAppointments = async (filters?: IFiltersAppointments): Promise<IAppointment[]> => {
  const response = await client.query({
    query: gql`
      query($filters: FiltersAppointmentInputType) {
        appointments(filters: $filters) {
          appointments {
            id
            date
            period
            hour
            professional_id
            professional {
              id
              user {
                name
                roles {
                  name
                }
              }
            }
            pet {
              id
              name
              weight
              specie {
                name
              }
              breed {
                name
                size
              }
            }
            customer {
              user {
                name
                id
              }
            }
            service {
              name
              category_id
            }
            address {
              cep
              city
              state
              street
              neighborhood
              number
              complement
            }
          }
        }
      }
    `,
    variables: {
      filters,
    },
  });
  return response.data.appointments.appointments;
};

const searchAppointmentsToModal = async (filters?: IFiltersAppointments): Promise<IAppointment[]> => {
  const response = await client.query({
    // O hooks/Auth troca client.defaultOptions inteiro e o 'no-cache' do
    // client se perde; sem isto, buscar de novo devolveria o status em cache.
    fetchPolicy: 'network-only',
    query: gql`
      query($filters: FiltersAppointmentInputType) {
        appointments(filters: $filters) {
          appointments {
            id
            date
            period
            hour
            professional_id
            observations
            status
            pet {
              id
              name
              weight
              temperament
              specie {
                name
              }
              breed {
                name
                size
              }
              getPermanetObservations {
                pet_observations {
                  id
                  pet_id
                  description
                }
              }
            }
            customer {
              user {
                name
                id
                getPermanetObservations {
                  customer_observations {
                    id
                    user_id
                    description
                  }
                }
              }
            }
            service {
              name
              category_id
            }
            address {
              cep
              city
              state
              street
              neighborhood
              number
              complement
            }
            additionals {
              name
            }
          }
        }
      }
    `,
    variables: {
      filters,
    },
  });
  return response.data.appointments.appointments;
};



const searchProfessionals = async (): Promise<IProfessional[]> => {
  const response = await client.query({
    query: gql`
    query($filters: FiltersProfessionalInputType) {
      professionals(filters: $filters) {
        id
        status
        user {
          name
          roles {
            name
          }
        }
      }
    }
    `,
    variables: {
      filters: {
        where: {
          status: true
        }
      }
    }
  });
  return response.data.professionals;
};

const updateAppointment = async (appointment: Partial<IAppointment>): Promise<IAppointment> => {
  const response = await client.mutate({
    mutation: gql`
      mutation($appointment: AppointmentUpdateInputType!) {
        updateAppointment(appointment: $appointment) {
          id
          hour
          professional_id
        }
    }
    `,
    variables: {
      appointment: {
        id: appointment.id,
        professional_id: appointment.professional_id,
        hour: appointment.hour,
      },
    },
  });
  return response.data.updateAppointment;
};

export interface IUserStats {
  total_appointments: number | null;
  total_completed: number | null;
  total_canceled: number | null;
  total_grooming: number | null;
  total_vet: number | null;
  total_vaccines: number | null;
  total_pets: number | null;
  total_ratings: number | null;
  rating_average: number | null;
  rating_max: number | null;
  rating_min: number | null;
  last_appointment_date: string | null;
  last_grooming_date: string | null;
  last_vaccine_date: string | null;
  last_vet_date: string | null;
  most_used_service_name: string | null;
}

const getUserStats = async (userId: string): Promise<IUserStats> => {
  const response = await client.query({
    query: gql`
      query GetUserStats($userId: String!) {
        getUser(id: $userId) {
          customer {
            stats {
              total_appointments
              total_completed
              total_canceled
              total_grooming
              total_vet
              total_vaccines
              total_pets
              total_ratings
              rating_average
              rating_max
              rating_min
              last_appointment_date
              last_grooming_date
              last_vaccine_date
              last_vet_date
              most_used_service_name
            }
          }
        }
      }
    `,
    variables: { userId },
  });
  return response.data.getUser.customer.stats;
};

export interface IAppointmentLog {
  description: string;
  created_at: string;
  user: { name: string } | null;
}

const getAppointmentLogs = async (appointmentId: string): Promise<IAppointmentLog[]> => {
  const response = await client.query({
    query: gql`
      query AppointmentLogs($appointmentId: String!) {
        appointmentLogs(appointmentId: $appointmentId) {
          description
          created_at
          user {
            name
          }
        }
      }
    `,
    variables: { appointmentId },
  });
  return response.data.appointmentLogs;
};

export { searchAppointments, searchAppointmentsToModal, searchProfessionals, updateAppointment, getUserStats, getAppointmentLogs };
